import { CommonUtils } from "../../util/common-utils";
import { Actions } from "../../constants/actions";
import { translator } from "../../translation/translator";
import { ChoicesControlForm } from "./choices-control-form";
import { ChoicesControlHandler } from "./choices-control-handler";
import { ChoicesControlListener } from "./choices-control-listener";
import { ChoicesControlRender } from "./choices-control-render";
import { ChoicesControlUtils, type ChoicesInstance, type ChoicesStringListValue } from "./choices-control-utils";

type ChoicesTemplateFactory = (markup: string) => string;

type ChoicesClassNames = {
    item?: string;
    highlightedState?: string;
    itemSelectable?: string;
    button?: string;
};

type ChoicesItemData = {
    label?: string | null | undefined;
    value?: string | null | undefined;
    id?: string | null | undefined;
    placeholder?: boolean;
    disabled?: boolean;
    highlighted?: boolean;
};

type ChoicesConstructorOptions = {
    removeItemButton: boolean;
    searchEnabled: boolean;
    placeholder: boolean;
    placeholderValue?: string;
    itemSelectText: string;
    shouldSort: boolean;
    position: "bottom";
    allowHTML: boolean;
    duplicateItemsAllowed: boolean;
    addItems: boolean;
    addItemFilter: (value: string | null | undefined) => boolean;
    callbackOnCreateTemplates?: (template: ChoicesTemplateFactory) => {
        item: (classNames: ChoicesClassNames | undefined, data: ChoicesItemData | undefined) => string;
    };
};

type ChoicesConstructor = new (select: HTMLSelectElement, options: ChoicesConstructorOptions) => ChoicesInstance;

class ChoicesControlSetup {
    private readonly root: ParentNode;

    public constructor(root: ParentNode = document) {
        this.root = root;
    }

    public static init(root: ParentNode = document): void {
        new ChoicesControlSetup(root).init();
    }

    public init(): void {
        const selects = ChoicesControlForm.getSelects(this.root);
        if (selects.length === 0) return;

        selects.forEach((select) => {
            this.initOne(select);
        });
    }

    public initOne(select: HTMLSelectElement): void {
        const internalSelect = ChoicesControlUtils.asInternalSelect(select);

        ChoicesControlListener.teardown(internalSelect);
        ChoicesControlSetup.applyTranslatedChoicesPlaceholder(select);

        const bindWrapper = ChoicesControlForm.getBindWrapper(select);
        const bindTo = bindWrapper instanceof HTMLElement ? bindWrapper.dataset.choicesBindTo : "";
        const bindFormat = bindWrapper instanceof HTMLElement ? bindWrapper.dataset.choicesBindFormat || "" : "";

        const hidden = bindTo && bindWrapper ? ChoicesControlForm.getBindHiddenInput(bindWrapper, bindTo) : null;

        if (hidden) {
            const initialValues = ChoicesControlUtils.normalizeStringList(hidden.value);
            Array.from(select.options).forEach((option) => {
                option.selected = false;
            });
            ChoicesControlRender.ensureOptionsAndSelect(select, initialValues);
        }

        const instance = ChoicesControlSetup.initChoices(select);
        if (!instance) return;

        ChoicesControlListener.registerClearButton(select, instance);
        ChoicesControlListener.registerToggle(select, instance);
        ChoicesControlListener.registerShowDropdown(select, instance);
        ChoicesControlListener.registerTranslatedPlaceholder(select, instance);

        if (hidden) {
            const initialValues = ChoicesControlUtils.normalizeStringList(hidden.value);
            if (initialValues.length > 0 && typeof instance.setChoiceByValue === "function") {
                initialValues.forEach((value) => instance.setChoiceByValue?.(value));
            }

            ChoicesControlListener.registerHiddenSync(select, hidden, bindFormat);
        }

        ChoicesControlListener.registerUiState(select, instance);
    }

    public static destroyOne(select: HTMLSelectElement): void {
        const internalSelect = ChoicesControlUtils.asInternalSelect(select);

        ChoicesControlListener.teardown(internalSelect);

        const instanceLike = internalSelect.__choicesInstance;
        if (instanceLike?.destroy && typeof instanceLike.destroy === "function") {
            try {
                instanceLike.destroy();
            } catch {
                // ignore destroy errors
            }
        }
        delete internalSelect.__choicesInstance;
    }

    public static destroyAll(root: ParentNode = document): void {
        const selects = ChoicesControlForm.getAllSelects(root);
        selects.forEach((select) => {
            ChoicesControlSetup.destroyOne(select);
        });
    }

    public static normalizeStringList(value: ChoicesStringListValue): string[] {
        return ChoicesControlUtils.normalizeStringList(value);
    }

    public static getSelectedValues(select: HTMLSelectElement): string[] {
        return ChoicesControlForm.getSelectedValues(select);
    }

    public static clearSelection(select: HTMLSelectElement, instance: ChoicesInstance | null): void {
        ChoicesControlHandler.clearSelection(select, instance);
    }

    public static applyTranslatedChoicesPlaceholder(select: HTMLSelectElement): void {
        const key = select.getAttribute("data-translate-choices-placeholder");
        if (!key) return;

        const translated = translator.translate(key);
        if (translated) {
            ChoicesControlRender.renderChoicesPlaceholder(select, translated);
        }
    }

    public static initChoices(select: HTMLSelectElement): ChoicesInstance | null {
        const Choices = Reflect.get(globalThis, "Choices") as ChoicesConstructor | undefined;
        if (typeof Choices !== "function") {
            console.error("Choices.js is not loaded");
            return null;
        }

        const internalSelect = ChoicesControlUtils.asInternalSelect(select);
        const existing = internalSelect.__choicesInstance;
        if (existing) return existing;

        const isMultiple = select.multiple;
        const allowCreate = select.dataset.choicesAllowCreate === "true";
        const placeholder = select.dataset.choicesPlaceholder || "";

        const instance = new Choices(select, {
            removeItemButton: isMultiple,
            searchEnabled: true,
            placeholder: Boolean(placeholder),
            placeholderValue: placeholder || undefined,
            itemSelectText: "",
            shouldSort: false,
            position: "bottom",
            allowHTML: false,
            duplicateItemsAllowed: false,
            addItems: true,
            addItemFilter: (value: string | null | undefined) => {
                const normalizedValue = String(value ?? "").trim();
                if (!normalizedValue) return false;
                if (allowCreate) return true;

                return Array.from(select.options).some((option) => String(option.value).trim() === normalizedValue);
            },
            callbackOnCreateTemplates: isMultiple
                ? (template: ChoicesTemplateFactory) => ({
                      item: (classNames: ChoicesClassNames | undefined, data: ChoicesItemData | undefined) => {
                          const itemClass =
                              classNames && typeof classNames.item === "string" ? classNames.item : "choices__item";
                          const highlightedClass =
                              classNames && typeof classNames.highlightedState === "string"
                                  ? classNames.highlightedState
                                  : "is-highlighted";
                          const selectableClass =
                              classNames && typeof classNames.itemSelectable === "string"
                                  ? classNames.itemSelectable
                                  : "is-selectable";
                          const buttonClass =
                              classNames && typeof classNames.button === "string"
                                  ? classNames.button
                                  : "choices__button";

                          const rawLabel = String(data?.label ?? "");
                          const valueRaw = String(data?.value ?? "");
                          const matchingOption = valueRaw
                              ? Array.from(select.options).find((option) => option.value === valueRaw)
                              : undefined;
                          const translateKey = matchingOption?.getAttribute("data-translate") || "";
                          const translatedLabel = translateKey ? translator.translate(translateKey) : "";
                          const label = CommonUtils.escapeHtml(translatedLabel || rawLabel);
                          const valueEscaped = CommonUtils.escapeHtmlAttribute(String(data?.value ?? ""));
                          const idEscaped = CommonUtils.escapeHtmlAttribute(String(data?.id ?? ""));
                          const isSelectable = !data?.placeholder && !data?.disabled;
                          const highlighted = data?.highlighted ? highlightedClass : "";
                          const selectable = isSelectable ? selectableClass : "";
                          const deletable = isSelectable ? 'data-deletable=""' : "";
                          const removeButton = isSelectable
                              ? `<button type="button" class="${buttonClass} btn circle-clear picker-chip__remove" data-button data-action="${Actions.REMOVE}" aria-label="Remove"></button>`
                              : "";

                          return template(`
                              <div class="${itemClass} picker-chip ${selectable} ${highlighted}" data-item data-id="${idEscaped}" data-value="${valueEscaped}" aria-selected="true" ${deletable}>
                                  <span class="picker-chip__label">${label}</span>
                                  ${removeButton}
                              </div>
                          `);
                      },
                  })
                : undefined,
        });

        internalSelect.__choicesInstance = instance;
        return instance;
    }
}

const ChoicesControl = ChoicesControlSetup;

function initChoicesControls(root: ParentNode = document): void {
    ChoicesControlSetup.init(root);
}

export { ChoicesControlSetup, ChoicesControl, initChoicesControls };
